import { Cross2Icon } from '@radix-ui/react-icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import * as Dialog from '@radix-ui/react-dialog';
import { Popover, PopoverAnchor, PopoverContent, PopoverPortal } from '@radix-ui/react-popover';
import { toast } from 'sonner';
import { z } from 'zod';
import { FormField, TextInputField, TextareaField } from '@/components/form';
import { PUBLIC_READINESS_LABELS } from '@/components/public/properties';
import { trackPublicPropertyAction } from '@/lib/analytics/events';
import { useFormController } from '@/lib/forms/formController';
import { useSiteSettingsDetail } from '@/lib/query/resources/siteSettings';
import { submitLeadFromLanding } from '@/lib/services/leads';
import { notifyShowingBooked } from '@/lib/services/emailNotifications';
import { bookShowing, fetchAvailableSlots, type BookShowingResult } from '@/lib/services/showings';
import { saveShowingRequest } from '@/lib/storage/showings';
import { createCalendarInvite } from '@/lib/utils/calendar';
import { formatUnitLabel, humanizeAddressText } from '@/lib/utils/propertyFormatting';
import { applyTemplate } from '@/lib/utils/textTemplates';
import { mergeContentWithDefaults } from '@/lib/utils/contentDefaults';
import {
  derivePipelineStatusFromShowing,
  type LeadPipelineStatus,
  type ShowingStatus,
} from '@/lib/types/showing';
import type { PublicPropertySummary, PublishedUnitSummary } from '@/lib/types/publicProperty';
import type { UnitAvailabilityStatus } from '@/lib/types/property';
import type { LeadLandingPayload } from '@/lib/types/lead';
import type { ShowingDialogContent } from '@/lib/types/siteSettings';

export const LEASEABLE_UNIT_STATUSES: ReadonlySet<UnitAvailabilityStatus> = new Set(['available']);

export const SHOWING_TRIGGER_BUTTON_CLASS =
  'inline-flex items-center justify-center rounded-xl bg-brand-accent px-5 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-brand-accent/90 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent disabled:cursor-not-allowed disabled:opacity-60';

export const SHOWING_TRIGGER_BUTTON_ON_DARK_CLASS =
  'inline-flex items-center justify-center rounded-xl bg-brand-accent px-5 py-3 text-sm font-semibold text-white shadow-md shadow-black/20 transition hover:bg-brand-accent/90 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent disabled:cursor-not-allowed disabled:opacity-60';

export const SHOWING_TRIGGER_BUTTON_OUTLINE_CLASS =
  'inline-flex items-center justify-center rounded-xl bg-brand-accent px-5 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-brand-accent/90 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent disabled:cursor-not-allowed disabled:opacity-60';

type TriggerAppearance = 'default' | 'onDark' | 'outline';

const SHOWING_TRIGGER_BUTTON_VARIANTS: Record<TriggerAppearance, string> = {
  default: SHOWING_TRIGGER_BUTTON_CLASS,
  onDark: SHOWING_TRIGGER_BUTTON_ON_DARK_CLASS,
  outline: SHOWING_TRIGGER_BUTTON_OUTLINE_CLASS,
};

const PORTAL_INVITE_STORAGE_KEY = 'showing_portal_invites';

const DEFAULT_SHOWING_DIALOG_CONTENT: ShowingDialogContent = {
  title: 'Schedule a showing',
  description:
    'No account needed—share your contact details and pick an available weekday time. We confirm instantly and tailor the visit to your notes.',
  stepIndicatorTemplate: 'Step {step} of {total}',
  selectedUnitLabel: 'Selected unit',
  selectedUnitDescription: "We'll use this context to tailor your tour.",
  steps: {
    contactLabel: 'Contact',
    scheduleLabel: 'Schedule',
    reviewLabel: 'Review',
  },
  fields: {
    firstNameLabel: 'First name',
    lastNameLabel: 'Last name',
    emailLabel: 'Work email',
    phoneLabel: 'Phone',
    dateLabel: 'Date',
    timeLabel: 'Time',
    messageLabel: 'Message',
  },
  fieldHints: {
    dateHint: 'Weekdays only',
    timeHint: 'Only available slots are shown. Weekday visits preferred.',
  },
  messagePlaceholder: 'Enter questions or tour preferences (optional)',
  review: {
    title: 'Review and confirm',
    contactLabel: 'Contact',
    slotLabel: 'Slot',
    unitLabel: 'Unit',
    messageLabel: 'Message',
    confirmationNote: "We'll email a confirmation and contact you if anything changes.",
    guidanceNext: 'We confirm your tour instantly after you submit.',
    guidanceReview: 'Review details before finishing.',
  },
  buttons: {
    cancelLabel: 'Cancel',
    backLabel: 'Back',
    nextLabel: 'Next',
    confirmLabel: 'Confirm booking',
    doneLabel: 'Done',
  },
  errors: {
    contactMissing: 'Add your name, email, and phone to continue.',
    scheduleMissing: 'Pick an available weekday time.',
    submitError: 'We could not submit your request. Please try again.',
  },
  success: {
    title: 'Tour confirmed',
    description: "We've reserved your slot and generated a calendar invite.",
    timeLabel: 'Time',
    emailLabel: 'Email',
    nameLabel: 'Name',
    messageLabel: 'Message',
    calendarLabel: 'Add to calendar (.ics)',
    portalInviteQueued:
      'Portal invite will be emailed to {email}. The link expires in 14 days, and we only send one invite per email.',
    portalInviteSent:
      'A portal invite has already been sent for this email. Reuse your existing link or request a new one if it expired.',
  },
  unavailableTooltip:
    'This unit is not currently available. Check back soon or favorite it for updates.',
};

interface UnitShowingTriggerButtonProps {
  summary: PublicPropertySummary;
  unit: PublishedUnitSummary;
  label?: string;
  className?: string;
  onCtaClick?: (unit: PublishedUnitSummary) => void;
  onCtaBlocked?: (unit: PublishedUnitSummary, reason: string | null) => void;
  appearance?: TriggerAppearance;
}

export function UnitShowingTriggerButton({
  summary,
  unit,
  label = 'Schedule Showing',
  className,
  onCtaClick,
  onCtaBlocked,
  appearance = 'default',
}: UnitShowingTriggerButtonProps): JSX.Element {
  const siteSettingsQuery = useSiteSettingsDetail();
  const showingContent = mergeContentWithDefaults(
    siteSettingsQuery.data?.publicContent?.showingDialog,
    DEFAULT_SHOWING_DIALOG_CONTENT,
  );
  const [unavailableOpen, setUnavailableOpen] = useState(false);
  const unavailablePopoverId = `unit-cta-${useId()}`;
  const leaseable = LEASEABLE_UNIT_STATUSES.has(unit.availabilityStatus);
  const variantClassName = SHOWING_TRIGGER_BUTTON_VARIANTS[appearance];
  const triggerClassName = clsx(variantClassName, className);

  if (!leaseable) {
    const readinessLabel = PUBLIC_READINESS_LABELS[unit.availabilityStatus];
    const reason = `Unit is currently ${readinessLabel.toLowerCase()}.`;

    return (
      <Popover open={unavailableOpen} onOpenChange={setUnavailableOpen}>
        <PopoverAnchor asChild>
          <button
            type="button"
            className={clsx(triggerClassName, 'cursor-not-allowed opacity-60')}
            aria-disabled="true"
            aria-haspopup="dialog"
            aria-controls={unavailablePopoverId}
            aria-expanded={unavailableOpen}
            onMouseEnter={() => {
              setUnavailableOpen(true);
            }}
            onMouseLeave={() => {
              setUnavailableOpen(false);
            }}
            onFocus={() => {
              setUnavailableOpen(true);
            }}
            onBlur={() => {
              setUnavailableOpen(false);
            }}
            onClick={() => {
              setUnavailableOpen(true);
              onCtaBlocked?.(unit, reason);
              trackPublicPropertyAction({
                propertyId: summary.id,
                propertyCode: summary.propertyCode,
                unitId: unit.id,
                readiness: unit.availabilityStatus,
                action: 'unit_cta_blocked',
                reason,
              });
            }}
          >
            {label}
          </button>
        </PopoverAnchor>
        <PopoverPortal>
          <PopoverContent
            id={unavailablePopoverId}
            side="top"
            align="center"
            sideOffset={8}
            collisionPadding={12}
            aria-label="Unit availability help"
            className="max-w-xs rounded-xl bg-black/80 px-3 py-2 text-xs text-white shadow-lg"
          >
            <div className="flex flex-col gap-2">
              <span>{showingContent.unavailableTooltip}</span>
              <Link
                to="/availabilities#availability-alerts"
                className="text-xs font-semibold uppercase tracking-wide text-white underline underline-offset-4"
              >
                Get availability alerts
              </Link>
            </div>
          </PopoverContent>
        </PopoverPortal>
      </Popover>
    );
  }

  return (
    <UnitEngagementDialog
      summary={summary}
      unit={unit}
      triggerLabel={label}
      triggerClassName={triggerClassName}
      onOpen={() => {
        trackPublicPropertyAction({
          propertyId: summary.id,
          propertyCode: summary.propertyCode,
          unitId: unit.id,
          readiness: unit.availabilityStatus,
          action: 'unit_cta_click',
        });
        trackPublicPropertyAction({
          propertyId: summary.id,
          propertyCode: summary.propertyCode,
          unitId: unit.id,
          readiness: unit.availabilityStatus,
          action: 'unit_showing_start',
        });
        onCtaClick?.(unit);
      }}
    />
  );
}

interface UnitEngagementDialogProps {
  summary: PublicPropertySummary;
  unit: PublishedUnitSummary;
  triggerLabel: string;
  triggerClassName: string;
  onOpen?: () => void;
  onSubmitComplete?: () => void;
}

const showingFormSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required.'),
  lastName: z.string().trim().min(1, 'Last name is required.'),
  email: z.string().trim().min(1, 'Work email is required.'),
  phone: z.string().trim().min(1, 'Phone is required.'),
  date: z.string().trim().min(1, 'Pick a weekday.'),
  time: z.string().trim().min(1, 'Select an available time.'),
  message: z.string().optional().default(''),
});

type ShowingFormValues = z.infer<typeof showingFormSchema>;
type ShowingFormErrors = Partial<Record<keyof ShowingFormValues, string>>;
type ShowingFieldKey = 'firstName' | 'lastName' | 'email' | 'phone' | 'date' | 'time';

export const TIME_SLOTS: Array<{ value: string; label: string }> = Array.from({ length: 9 }).map(
  (_, index) => {
    const hour = 9 + index; // 9:00 through 17:00 inclusive
    const value = `${hour.toString().padStart(2, '0')}:00`;
    return { value, label: formatHourLabel(hour) };
  },
);

function buildInitialFormValues(): ShowingFormValues {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    date: computeMinDate(),
    time: '',
    message: '',
  };
}

function normalizeSubmission(values: ShowingFormValues): ShowingFormValues {
  return {
    ...values,
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    email: values.email.trim().toLowerCase(),
    phone: values.phone.trim(),
    time: values.time.trim(),
    date: values.date.trim(),
    message: values.message.trim(),
  };
}

function formatFullName(firstName: string, lastName: string): string {
  const normalizedFirst = firstName.trim();
  const normalizedLast = lastName.trim();

  if (normalizedFirst.length === 0) return normalizedLast;
  if (normalizedLast.length === 0) return normalizedFirst;
  return `${normalizedFirst} ${normalizedLast}`;
}

export function UnitEngagementDialog({
  summary,
  unit,
  triggerLabel,
  triggerClassName,
  onOpen,
  onSubmitComplete,
}: UnitEngagementDialogProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [successDetails, setSuccessDetails] = useState<{
    date: string;
    time: string;
    email: string;
    name: string;
    message: string | null;
  } | null>(null);
  const [calendarInviteUrl, setCalendarInviteUrl] = useState<string | null>(null);
  const calendarInviteUrlRef = useRef<string | null>(null);
  const [portalInviteStatus, setPortalInviteStatus] = useState<'queued' | 'alreadySent' | null>(
    null,
  );
  const availableSlotsRef = useRef<string[]>([]);
  const siteSettingsQuery = useSiteSettingsDetail();
  const showingContent = mergeContentWithDefaults(
    siteSettingsQuery.data?.publicContent?.showingDialog,
    DEFAULT_SHOWING_DIALOG_CONTENT,
  );
  const unitDisplay = formatUnitLabel(unit);
  const propertyAddressLabel = formatSummaryAddress(summary);
  const propertyDisplayLabel =
    propertyAddressLabel.length > 0
      ? propertyAddressLabel
      : humanizeAddressText(summary.propertyCode);

  const validateShowingForm = useCallback(
    (values: ShowingFormValues): ShowingFormErrors => {
      const errors: ShowingFormErrors = {};
      const result = showingFormSchema.safeParse(values);
      if (!result.success) {
        result.error.issues.forEach((issue) => {
          const pathKey = issue.path.at(0);
          if (typeof pathKey === 'string') {
            errors[pathKey as keyof ShowingFormValues] = issue.message;
          }
        });
      }

      if (!isValidScheduledDate(values.date)) {
        errors.date = 'Pick a weekday.';
      }

      const slots = availableSlotsRef.current;
      const selectedTime = values.time.trim();
      if (selectedTime.length === 0) {
        errors.time = 'Select an available time.';
      } else if (
        stepIndex === 1 &&
        slots.length > 0 &&
        !slots.some((slot) => normalizeTimeToken(slot) === normalizeTimeToken(selectedTime))
      ) {
        errors.time = 'Select an available time.';
      }

      return errors;
    },
    [stepIndex],
  );

  const cleanupCalendarInvite = useCallback((resetState: boolean = false): void => {
    if (calendarInviteUrlRef.current !== null) {
      URL.revokeObjectURL(calendarInviteUrlRef.current);
      calendarInviteUrlRef.current = null;
    }
    if (resetState) {
      setCalendarInviteUrl(null);
    }
  }, []);

  const steps = useMemo(
    () => [
      showingContent.steps.contactLabel,
      showingContent.steps.scheduleLabel,
      showingContent.steps.reviewLabel,
    ],
    [showingContent.steps],
  );

  useEffect(() => {
    return () => {
      cleanupCalendarInvite();
    };
  }, [cleanupCalendarInvite]);

  const bookShowingMutation = useMutation<BookShowingResult, unknown, ShowingFormValues>({
    mutationFn: async (submission) => {
      return await bookShowing({
        firstName: submission.firstName,
        lastName: submission.lastName,
        email: submission.email,
        phone: submission.phone,
        unitId: unit.id,
        date: submission.date,
        time: submission.time,
        message: submission.message,
      });
    },
    onSuccess: (result, submission) => {
      const fullName = formatFullName(submission.firstName, submission.lastName);
      const showingDetails = normalizeShowingSchedule(result.showing);
      const scheduledDate =
        showingDetails.scheduledAt !== null ? showingDetails.scheduledAt.slice(0, 10) : null;
      const scheduledTime =
        showingDetails.scheduledAt !== null ? showingDetails.scheduledAt.slice(11, 16) : null;
      const finalDate = scheduledDate ?? showingDetails.preferredDate ?? submission.date;
      const finalTime = scheduledTime ?? showingDetails.preferredTime ?? submission.time;
      const showingStatus = result.showing.status as ShowingStatus;
      const pipelineStatus = derivePipelineStatusFromShowing(
        showingStatus,
        result.lead.pipeline_status as LeadPipelineStatus | undefined,
      );
      const trimmedMessage = submission.message.trim();

      setSuccessDetails({
        date: finalDate,
        time: finalTime,
        email: submission.email,
        name: fullName,
        message: trimmedMessage.length > 0 ? trimmedMessage : null,
      });

      const attendeeName = fullName.length > 0 ? fullName : submission.email;
      const startDate = buildEventStart(finalDate, finalTime);
      const eventStart =
        startDate ??
        buildEventStart(
          showingDetails.preferredDate ?? submission.date,
          showingDetails.preferredTime ?? submission.time,
        ) ??
        new Date();
      const invite = createCalendarInvite({
        title: `Showing – ${unitDisplay} at ${propertyDisplayLabel}`,
        description:
          trimmedMessage.length > 0
            ? trimmedMessage
            : `Tour for ${unitDisplay} at ${propertyDisplayLabel}`,
        location: propertyDisplayLabel,
        start: eventStart,
        durationMinutes: 60,
        organizerName: 'Strashin Developments',
        organizerEmail: unit.contactEmail,
        attendeeName: attendeeName.length > 0 ? attendeeName : undefined,
        attendeeEmail: submission.email.length > 0 ? submission.email : undefined,
        timezone: 'America/Toronto',
        uid: showingDetails.id ?? undefined,
      });
      cleanupCalendarInvite();
      const inviteBlob = new Blob([invite], { type: 'text/calendar' });
      const inviteUrl = URL.createObjectURL(inviteBlob);
      calendarInviteUrlRef.current = inviteUrl;
      setCalendarInviteUrl(inviteUrl);

      saveShowingRequest({
        id: showingDetails.id ?? result.showing.id,
        leadId: result.showing.lead_id,
        propertyId: summary.id,
        propertyCode: summary.propertyCode,
        unitId: unit.id,
        unitLabel: unitDisplay,
        visitorName: fullName,
        visitorEmail: submission.email,
        visitorPhone: submission.phone,
        preferredDate: finalDate,
        preferredTime: finalTime,
        message: trimmedMessage.length > 0 ? trimmedMessage : null,
        status: showingStatus,
        pipelineStatus,
        createdAt: result.showing.created_at,
        updatedAt: result.showing.updated_at,
      });

      void notifyShowingBooked({
        showingId: showingDetails.id ?? result.showing.id,
        leadEmail: submission.email,
        leadName: fullName,
        scheduledDate: finalDate,
        scheduledTime: finalTime,
        unitId: unit.id,
        propertyId: summary.id,
      });

      if (submission.email.length > 0) {
        const alreadySent = hasPortalInviteBeenSent(submission.email);
        if (!alreadySent) {
          markPortalInviteSent(submission.email);
          setPortalInviteStatus('queued');
          trackPublicPropertyAction({
            propertyId: summary.id,
            propertyCode: summary.propertyCode,
            unitId: unit.id,
            readiness: unit.availabilityStatus,
            action: 'unit_portal_invite_queued',
            detail: { email: submission.email },
          });
        } else {
          setPortalInviteStatus('alreadySent');
        }
      } else {
        setPortalInviteStatus(null);
      }

      toast.success(showingContent.success.title);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error && typeof error.message === 'string'
          ? error.message
          : showingContent.errors.submitError;
      toast.error(message);
    },
  });

  const handleFormSubmit = useCallback(
    async (values: ShowingFormValues) => {
      const submission = normalizeSubmission(values);
      const fullName = formatFullName(submission.firstName, submission.lastName);
      const noteLines: string[] = [];
      noteLines.push(`Showing booked for ${submission.date} ${submission.time}`);
      if (submission.message.length > 0) {
        noteLines.push(`Message: ${submission.message}`);
      }

      const payload: LeadLandingPayload = {
        fullName,
        firstName: submission.firstName,
        lastName: submission.lastName,
        email: submission.email,
        phone: submission.phone,
        campaignSource: 'public_showing',
        campaignMedium: 'public_site',
        desiredPropertyId: summary.id,
        desiredUnitId: unit.id,
        budgetMonthly: null,
        preferredMoveInDate: null,
        notes: noteLines.join('\\n'),
      };

      try {
        await submitLeadFromLanding({ values: payload });

        trackPublicPropertyAction({
          propertyId: summary.id,
          propertyCode: summary.propertyCode,
          unitId: unit.id,
          readiness: unit.availabilityStatus,
          action: 'unit_showing_request',
          detail: { date: submission.date, time: submission.time },
        });

        await bookShowingMutation.mutateAsync(submission);

        onSubmitComplete?.();
      } catch (error: unknown) {
        trackPublicPropertyAction({
          propertyId: summary.id,
          propertyCode: summary.propertyCode,
          unitId: unit.id,
          readiness: unit.availabilityStatus,
          action: 'unit_showing_error',
          detail: { message: error instanceof Error ? error.message : 'unknown_error' },
        });
        toast.error(showingContent.errors.submitError);
        throw error;
      }
    },
    [
      bookShowingMutation,
      onSubmitComplete,
      summary.id,
      summary.propertyCode,
      unit.availabilityStatus,
      unit.id,
    ],
  );

  const form = useFormController<ShowingFormValues>({
    initialValues: buildInitialFormValues(),
    validate: validateShowingForm,
    onSubmit: handleFormSubmit,
    mapSubmitError: (error) => ({
      message: error instanceof Error ? error.message : showingContent.errors.submitError,
    }),
  });

  const selectedUnitLabel = formatSelectedUnitLabel(summary, unit);

  const slotsQuery = useQuery({
    queryKey: ['showing-available-slots', unit.id, form.values.date],
    queryFn: async ({ signal }) => {
      return await fetchAvailableSlots({ unitId: unit.id, date: form.values.date, signal });
    },
    enabled: open && stepIndex === 1 && form.values.date.trim().length > 0,
    staleTime: 30_000,
  });

  const availableSlots = useMemo(() => slotsQuery.data ?? [], [slotsQuery.data]);

  useEffect(() => {
    availableSlotsRef.current = availableSlots;
  }, [availableSlots]);

  const pickErrors = useCallback(
    (errors: ShowingFormErrors, keys: ShowingFieldKey[]): ShowingFormErrors => {
      const selected: ShowingFormErrors = {};
      keys.forEach((key) => {
        const message = errors[key];
        if (typeof message === 'string' && message.length > 0) {
          selected[key] = message;
        }
      });
      return selected;
    },
    [],
  );

  const markFieldsTouched = useCallback(
    (keys: ShowingFieldKey[]) => {
      keys.forEach((key) => {
        form.field(key).onBlur();
      });
    },
    [form],
  );

  const collectValidationErrors = useCallback((): ShowingFormErrors => {
    const errors = validateShowingForm(form.values);
    form.setErrors(errors);
    return errors;
  }, [form, validateShowingForm]);

  const isSubmitting = form.isSubmitting || bookShowingMutation.isPending;

  const handleOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) {
      cleanupCalendarInvite(true);
      setPortalInviteStatus(null);
      setSuccessDetails(null);
      form.reset(buildInitialFormValues());
      form.setErrors({});
    }
    setOpen(nextOpen);
    if (nextOpen) {
      onOpen?.();
      cleanupCalendarInvite(true);
      setStepIndex(0);
      setSuccessDetails(null);
      setPortalInviteStatus(null);
      form.reset(buildInitialFormValues());
      form.setErrors({});
    }
  };

  const handleNext = (): void => {
    const allErrors = collectValidationErrors();
    if (stepIndex === 0) {
      const contactErrors = pickErrors(allErrors, ['firstName', 'lastName', 'email', 'phone']);
      if (Object.keys(contactErrors).length > 0) {
        markFieldsTouched(['firstName', 'lastName', 'email', 'phone']);
        toast.error(showingContent.errors.contactMissing);
        return;
      }
    }
    if (stepIndex === 1) {
      const scheduleErrors = pickErrors(allErrors, ['date', 'time']);
      if (Object.keys(scheduleErrors).length > 0) {
        markFieldsTouched(['date', 'time']);
        toast.error(showingContent.errors.scheduleMissing);
        return;
      }
    }
    form.setErrors({});
    setStepIndex((current) => Math.min(current + 1, steps.length - 1));
  };

  const handleSubmit = async (event?: { preventDefault?: () => void }): Promise<void> => {
    await form.handleSubmit(event);
  };

  const handleConfirm = async (): Promise<void> => {
    await handleFormSubmit(form.values);
  };

  const firstNameField = form.field('firstName');
  const lastNameField = form.field('lastName');
  const emailField = form.field('email');
  const phoneField = form.field('phone');
  const dateField = form.field('date');
  const timeField = form.field('time');
  const messageField = form.field('message');

  const reviewName = formatFullName(firstNameField.value, lastNameField.value);
  const reviewMessage = (typeof messageField.value === 'string' ? messageField.value : '').trim();
  const reviewDate = typeof dateField.value === 'string' ? dateField.value : '';
  const reviewTime = typeof timeField.value === 'string' ? timeField.value : '';

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className={triggerClassName}
          onClick={() => {
            setOpen(true);
            trackPublicPropertyAction({
              propertyId: summary.id,
              propertyCode: summary.propertyCode,
              unitId: unit.id,
              readiness: unit.availabilityStatus,
              action: 'unit_showing_start',
            });
          }}
        >
          {triggerLabel}
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content className="fixed inset-x-4 top-6 z-50 mx-auto flex max-h-[calc(100dvh-3rem)] max-w-2xl flex-col gap-6 overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl sm:inset-x-0 sm:top-20 sm:max-h-[calc(100dvh-10rem)]">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <Dialog.Title className="text-xl font-semibold text-brand-primary">
                {showingContent.title}
              </Dialog.Title>
              <Dialog.Description className="text-sm text-text-primary/70">
                {showingContent.description}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-surface-subtle text-text-primary/70 transition hover:border-brand-primary/50 hover:bg-brand-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
                aria-label="Close showing dialog"
              >
                <Cross2Icon className="h-4 w-4" aria-hidden />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex items-center justify-between sm:hidden">
            <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
              {applyTemplate(showingContent.stepIndicatorTemplate, {
                step: stepIndex + 1,
                total: steps.length,
              })}
            </span>
          </div>

          <div className="flex flex-col gap-2 rounded-2xl border border-surface-subtle bg-surface-subtle/40 p-3 text-sm text-text-primary/80">
            <span className="text-xs font-semibold uppercase tracking-wide text-brand-primary/70">
              {showingContent.selectedUnitLabel}
            </span>
            <span className="text-base font-semibold text-brand-primary">{selectedUnitLabel}</span>
            <span className="text-xs text-text-primary/60">
              {showingContent.selectedUnitDescription}
            </span>
          </div>

          <ol className="hidden items-center justify-center gap-3 text-xs font-semibold uppercase tracking-wide text-text-primary/50 sm:flex">
            {steps.map((label, index) => {
              const active = index === stepIndex;
              const complete = index < stepIndex;
              return (
                <li key={label} className="flex items-center gap-2">
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full border text-[11px] ${complete ? 'border-brand-primary bg-brand-primary text-white' : active ? 'border-brand-primary text-brand-primary' : 'border-surface-subtle text-text-primary/60'}`}
                  >
                    {index + 1}
                  </span>
                  <span className={active ? 'text-brand-primary' : ''}>{label}</span>
                  {index < steps.length - 1 ? (
                    <span className="bg-surface-strong h-px w-6" />
                  ) : null}
                </li>
              );
            })}
          </ol>

          {successDetails ? (
            <div
              className="flex flex-col gap-4 rounded-2xl border border-surface-subtle bg-surface-subtle/40 p-4"
              role="status"
              aria-live="polite"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary">
                  ✓
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-semibold text-brand-primary">
                    {showingContent.success.title}
                  </p>
                  <p className="text-xs text-text-primary/70">
                    {showingContent.success.description}
                  </p>
                </div>
              </div>
              <dl className="grid gap-2 text-sm text-text-primary/80 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                    {showingContent.success.timeLabel}
                  </dt>
                  <dd className="font-semibold text-text-primary">
                    {formatDateLabel(successDetails.date)} at {formatSlotLabel(successDetails.time)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                    {showingContent.success.emailLabel}
                  </dt>
                  <dd className="font-semibold text-text-primary">{successDetails.email}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                    {showingContent.success.nameLabel}
                  </dt>
                  <dd className="font-semibold text-text-primary">{successDetails.name}</dd>
                </div>
                {successDetails.message !== null ? (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                      {showingContent.success.messageLabel}
                    </dt>
                    <dd className="font-semibold text-text-primary">{successDetails.message}</dd>
                  </div>
                ) : null}
              </dl>
              {calendarInviteUrl !== null ? (
                <a
                  href={calendarInviteUrl}
                  download={`showing-${unitDisplay}-${successDetails.date}.ics`}
                  className="inline-flex min-h-11 items-center gap-2 self-start rounded-xl border border-brand-primary/30 bg-white px-4 py-2 text-sm font-semibold text-brand-primary shadow-sm transition hover:bg-brand-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                >
                  {showingContent.success.calendarLabel}
                </a>
              ) : null}
              {portalInviteStatus === 'queued' ? (
                <p className="text-xs text-text-primary/70">
                  {applyTemplate(showingContent.success.portalInviteQueued, {
                    email: successDetails.email,
                  })}
                </p>
              ) : null}
              {portalInviteStatus === 'alreadySent' ? (
                <p className="text-xs text-text-primary/70">
                  {applyTemplate(showingContent.success.portalInviteSent, {
                    email: successDetails.email,
                  })}
                </p>
              ) : null}
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="min-h-11 self-start rounded-xl bg-brand-accent px-5 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-brand-accent/90"
                >
                  {showingContent.buttons.doneLabel}
                </button>
              </Dialog.Close>
            </div>
          ) : (
            <form className="flex flex-col gap-4" onSubmit={handleSubmit} aria-live="polite">
              {stepIndex === 0 ? (
                <div className="grid gap-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <TextInputField
                      id="showing-first-name"
                      label={showingContent.fields.firstNameLabel}
                      required
                      value={firstNameField.value}
                      onChange={firstNameField.onChange}
                      onBlur={firstNameField.onBlur}
                      placeholder={showingContent.fields.firstNameLabel}
                      error={form.touched.has('firstName') ? form.errors.firstName : undefined}
                      autoComplete="given-name"
                      className="flex flex-col gap-2 text-sm text-text-primary/80 [&>label]:font-semibold [&>label]:text-brand-primary"
                    />
                    <TextInputField
                      id="showing-last-name"
                      label={showingContent.fields.lastNameLabel}
                      required
                      value={lastNameField.value}
                      onChange={lastNameField.onChange}
                      onBlur={lastNameField.onBlur}
                      placeholder={showingContent.fields.lastNameLabel}
                      error={form.touched.has('lastName') ? form.errors.lastName : undefined}
                      autoComplete="family-name"
                      className="flex flex-col gap-2 text-sm text-text-primary/80 [&>label]:font-semibold [&>label]:text-brand-primary"
                    />
                  </div>
                  <TextInputField
                    id="showing-work-email"
                    label={showingContent.fields.emailLabel}
                    type="email"
                    required
                    value={emailField.value}
                    onChange={emailField.onChange}
                    onBlur={emailField.onBlur}
                    placeholder={showingContent.fields.emailLabel}
                    error={form.touched.has('email') ? form.errors.email : undefined}
                    autoComplete="email"
                    className="flex flex-col gap-2 text-sm text-text-primary/80 [&>label]:font-semibold [&>label]:text-brand-primary"
                  />
                  <TextInputField
                    id="showing-phone"
                    label={showingContent.fields.phoneLabel}
                    type="tel"
                    required
                    value={phoneField.value}
                    onChange={phoneField.onChange}
                    onBlur={phoneField.onBlur}
                    placeholder={showingContent.fields.phoneLabel}
                    error={form.touched.has('phone') ? form.errors.phone : undefined}
                    autoComplete="tel"
                    className="flex flex-col gap-2 text-sm text-text-primary/80 [&>label]:font-semibold [&>label]:text-brand-primary"
                  />
                </div>
              ) : null}

              {stepIndex === 1 ? (
                <div className="flex flex-col gap-4">
                  <div className="grid gap-4">
                    <TextInputField
                      id="showing-date"
                      label={showingContent.fields.dateLabel}
                      type="date"
                      required
                      value={dateField.value}
                      min={computeMinDate()}
                      onChange={dateField.onChange}
                      onBlur={dateField.onBlur}
                      hint={showingContent.fieldHints.dateHint}
                      error={form.touched.has('date') ? form.errors.date : undefined}
                      className="flex flex-col gap-2 text-sm text-text-primary/80 [&>label]:font-semibold [&>label]:text-brand-primary"
                    />
                    <FormField
                      id="showing-time"
                      label={showingContent.fields.timeLabel}
                      required
                      hint={showingContent.fieldHints.timeHint}
                      error={form.touched.has('time') ? form.errors.time : undefined}
                      className="flex flex-col gap-2 text-sm text-text-primary/80 [&>label]:font-semibold [&>label]:text-brand-primary"
                    >
                      <select
                        value={timeField.value}
                        onChange={timeField.onChange}
                        onBlur={timeField.onBlur}
                        className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                        required
                      >
                        <option value="">{showingContent.fields.timeLabel}</option>
                        {availableSlots.map((slot) => (
                          <option key={slot} value={slot}>
                            {formatSlotLabel(slot)}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </div>
                  <TextareaField
                    id="showing-message"
                    label={showingContent.fields.messageLabel}
                    value={messageField.value}
                    onChange={messageField.onChange}
                    rows={3}
                    placeholder={showingContent.messagePlaceholder}
                    className="flex flex-col gap-2 text-sm text-text-primary/80 [&>label]:font-semibold [&>label]:text-brand-primary"
                  />
                </div>
              ) : null}

              {stepIndex === 2 ? (
                <div className="flex flex-col gap-3 rounded-xl border border-surface-subtle bg-surface-subtle/30 p-4 text-sm text-text-primary/80">
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary/70">
                    {showingContent.review.title}
                  </p>
                  <dl className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                        {showingContent.review.contactLabel}
                      </dt>
                      <dd className="font-semibold text-text-primary">
                        {reviewName} · {emailField.value}
                      </dd>
                      <dd className="text-xs text-text-primary/70">{phoneField.value}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                        {showingContent.review.slotLabel}
                      </dt>
                      <dd className="font-semibold text-text-primary">
                        {formatDateLabel(reviewDate)} at {formatSlotLabel(reviewTime)} (ET)
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                        {showingContent.review.unitLabel}
                      </dt>
                      <dd className="font-semibold text-text-primary">
                        {unitDisplay} · {propertyDisplayLabel}
                      </dd>
                    </div>
                    {reviewMessage.length > 0 ? (
                      <div className="sm:col-span-2">
                        <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                          {showingContent.review.messageLabel}
                        </dt>
                        <dd className="font-semibold text-text-primary">{reviewMessage}</dd>
                      </div>
                    ) : null}
                  </dl>
                  <p className="text-xs text-text-primary/60">
                    {showingContent.review.confirmationNote}
                  </p>
                </div>
              ) : null}

              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <div className="flex items-center gap-2 text-xs text-text-primary/60">
                  <span aria-live="polite">
                    {stepIndex === steps.length - 1
                      ? showingContent.review.guidanceReview
                      : showingContent.review.guidanceNext}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Dialog.Close asChild>
                    <button
                      type="button"
                      className="min-h-11 rounded-xl border border-surface-subtle px-4 py-2 text-sm font-semibold text-text-primary/80 transition hover:border-brand-primary/50 hover:bg-brand-primary/5"
                    >
                      {showingContent.buttons.cancelLabel}
                    </button>
                  </Dialog.Close>
                  {stepIndex > 0 ? (
                    <button
                      type="button"
                      onClick={() => {
                        setStepIndex((current) => Math.max(0, current - 1));
                      }}
                      className="min-h-11 rounded-xl border border-surface-subtle px-4 py-2 text-sm font-semibold text-text-primary/80 hover:border-brand-primary/50"
                    >
                      {showingContent.buttons.backLabel}
                    </button>
                  ) : null}
                  {stepIndex < steps.length - 1 ? (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="min-h-11 rounded-xl bg-brand-accent px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-brand-accent/90"
                    >
                      {showingContent.buttons.nextLabel}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleConfirm}
                      disabled={isSubmitting}
                      className="min-h-11 rounded-xl bg-brand-accent px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-brand-accent/90 disabled:opacity-70"
                    >
                      {isSubmitting
                        ? `${showingContent.buttons.confirmLabel}...`
                        : showingContent.buttons.confirmLabel}
                    </button>
                  )}
                </div>
              </div>
            </form>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function toNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  return value.length > 0 ? value : null;
}

function normalizeShowingSchedule(showing: BookShowingResult['showing']): {
  scheduledAt: string | null;
  preferredDate: string | null;
  preferredTime: string | null;
  id: string | null;
} {
  return {
    scheduledAt: toNonEmptyString(showing.scheduled_at),
    preferredDate: toNonEmptyString(showing.preferred_date),
    preferredTime: toNonEmptyString(showing.preferred_time),
    id: toNonEmptyString(showing.id),
  };
}

function computeMinDate(): string {
  const date = stripTime(new Date());
  date.setDate(date.getDate() + 1);
  while (date.getDay() === 0 || date.getDay() === 6) {
    date.setDate(date.getDate() + 1);
  }
  return formatDateInputValue(date);
}

function buildEventStart(dateLabel: string, timeLabel: string): Date | null {
  if (!dateLabel || !timeLabel) return null;
  const isoCandidate = `${dateLabel}T${timeLabel}:00`;
  const parsed = new Date(isoCandidate);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
}

function formatSummaryAddress(summary: PublicPropertySummary): string {
  const { address } = summary;
  const street = [
    humanizeAddressText(address.streetNumber),
    humanizeAddressText(address.streetName),
    humanizeAddressText(address.streetType),
    humanizeAddressText(address.streetDirection),
  ]
    .filter(Boolean)
    .join(' ');

  return [
    street,
    humanizeAddressText(address.city),
    humanizeAddressText(address.province),
    humanizeAddressText(address.postalCode),
  ]
    .filter(Boolean)
    .join(', ');
}

function parsePortalInviteEmails(): Set<string> {
  try {
    const raw = localStorage.getItem(PORTAL_INVITE_STORAGE_KEY);
    if (raw === null) return new Set<string>();
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const emails = parsed.filter((value): value is string => typeof value === 'string');
      return new Set(emails);
    }
    return new Set<string>();
  } catch (error) {
    console.error('Failed to parse portal invites storage', error);
    return new Set<string>();
  }
}

function hasPortalInviteBeenSent(email: string): boolean {
  const normalized = email.trim().toLowerCase();
  if (normalized.length === 0) return false;
  return parsePortalInviteEmails().has(normalized);
}

function markPortalInviteSent(email: string): void {
  const normalized = email.trim().toLowerCase();
  if (normalized.length === 0) return;
  const invites = parsePortalInviteEmails();
  invites.add(normalized);
  localStorage.setItem(PORTAL_INVITE_STORAGE_KEY, JSON.stringify(Array.from(invites)));
}

function isValidScheduledDate(value: string): boolean {
  if (value.trim().length === 0) {
    return false;
  }
  const selected = parseDateInputValue(value);
  if (selected === null) {
    return false;
  }
  const today = new Date();
  const day = selected.getDay();
  const isWeekend = day === 0 || day === 6;
  return selected >= stripTime(today) && !isWeekend;
}

function stripTime(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseDateInputValue(value: string): Date | null {
  const normalized = value.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }

  const parsed = new Date(year, month - 1, day);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function formatDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year.toString()}-${month}-${day}`;
}

function formatHourLabel(hour24: number): string {
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${String(hour12)}:00 ${period}`;
}

export function formatSelectedUnitLabel(
  summary: PublicPropertySummary,
  unit: PublishedUnitSummary,
): string {
  const { address } = summary;
  const street = `${address.streetNumber} ${address.streetName} ${address.streetType}`.trim();
  const formatter = new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 });
  const areaLabel = `${formatter.format(unit.areaSF)} sq ft`;
  return `#${formatUnitLabel(unit)}-${street}, ${areaLabel}`;
}

function formatSlotLabel(value: string): string {
  const parts = value.split(':');
  const hour = Number(parts[0]);
  const minute = parts[1] ?? '00';
  if (Number.isNaN(hour)) {
    return value;
  }
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12.toString()}:${minute.padStart(2, '0')} ${period}`;
}

function normalizeTimeToken(value: string): string {
  const [hour, minute] = value.trim().split(':');
  if (!hour) return value.trim();
  return `${hour.padStart(2, '0')}:${(minute ?? '00').padStart(2, '0')}`;
}

function formatDateLabel(value: string): string {
  if (!value) return 'Date not set';
  const parsed = parseDateInputValue(value);
  if (parsed === null) {
    return 'Date not set';
  }
  return parsed.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
